import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Attach JWT token automatically
api.interceptors.request.use(config => {
  const token = localStorage.getItem('charth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Sem isto o navegador pode reaproveitar a resposta antiga de um GET ao
  // reabrir o sistema (tela com dados velhos/vazios até deslogar e logar de
  // novo). O backend também manda `Cache-Control: no-store` na API — os dois
  // lados garantem que a tela sempre reflita o banco.
  if ((config.method || 'get').toLowerCase() === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
  }
  return config;
});

// A sessão acabou? 401 é o caso normal. O 403 só conta quando o corpo diz que o
// problema é o TOKEN — versões antigas do backend devolviam 403 para token
// expirado, e um navegador com bundle velho em cache ainda pode recebê-lo.
// Um 403 de permissão (requireRole) NÃO derruba a sessão.
export function isSessionLost(err) {
  const res = err?.response;
  if (!res) return false;                       // sem resposta = rede, não sessão
  if (res.status === 401) return true;
  if (res.status !== 403) return false;
  const data = res.data || {};
  return data.code === 'token_invalid' || /token .*(inv[áa]lido|expirado)/i.test(data.error || '');
}

export const SESSION_LOST_EVENT = 'charth:session-lost';

// Sessão perdida → limpa o que está guardado e avisa o App, que troca a tela
// para o login. Usamos um evento em vez de `location.href` porque a recarga
// descartava a marca de "sessão expirada" antes de a tela conseguir mostrá-la —
// e ainda jogava fora o que o usuário tivesse na tela.
export function dropSession() {
  localStorage.removeItem('charth_token');
  localStorage.removeItem('charth_user');
  try { sessionStorage.setItem('charth_session_expired', '1'); } catch {}
  try { window.dispatchEvent(new Event(SESSION_LOST_EVENT)); } catch {}
}

api.interceptors.response.use(
  r => r,
  err => {
    if (isSessionLost(err)) dropSession();
    return Promise.reject(err);
  }
);

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }).then(r => r.data),
  agentLogin: (email, password) => api.post('/auth/agent-login', { email, password }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }).then(r => r.data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }).then(r => r.data),
};

export const users = {
  list: () => api.get('/users').then(r => r.data),
  create: data => api.post('/users', data).then(r => r.data),
  update: (id, data) => api.put(`/users/${id}`, data).then(r => r.data),
  delete: id => api.delete(`/users/${id}`).then(r => r.data),
};

export const agents = {
  list: () => api.get('/agents').then(r => r.data),
  create: data => api.post('/agents', data).then(r => r.data),
  update: (id, data) => api.put(`/agents/${id}`, data).then(r => r.data),
  delete: id => api.delete(`/agents/${id}`).then(r => r.data),
};

export const alertSettings = {
  get: () => api.get('/settings/alerts').then(r => r.data),
  update: data => api.put('/settings/alerts', data).then(r => r.data),
};

export const suppliers = {
  list: (params) => api.get('/suppliers', { params }).then(r => r.data.rows !== undefined ? r.data : { rows: r.data, total: r.data.length, page: 1, pages: 1 }),
  get: id => api.get(`/suppliers/${id}`).then(r => r.data),
  create: data => api.post('/suppliers', data).then(r => r.data),
  update: (id, data) => api.put(`/suppliers/${id}`, data).then(r => r.data),
  delete: id => api.delete(`/suppliers/${id}`).then(r => r.data),
  importXlsx: fd => api.post('/suppliers/import', fd).then(r => r.data),
  uploadFiles: (id, fd) => api.post(`/suppliers/${id}/files`, fd).then(r => r.data),
  deleteFile: fileId => api.delete(`/supplier-files/${fileId}`).then(r => r.data),
};

export const quotations = {
  list: (params) => api.get('/quotations', { params }).then(r => r.data.rows !== undefined ? r.data : { rows: r.data, total: r.data.length, page: 1, pages: 1 }),
  get: id => api.get(`/quotations/${id}`).then(r => r.data),
  create: data => api.post('/quotations', data).then(r => r.data),
  update: (id, data) => api.put(`/quotations/${id}`, data).then(r => r.data),
  setStatus: (id, status) => api.patch(`/quotations/${id}/status`, { status }).then(r => r.data),
  delete: id => api.delete(`/quotations/${id}`).then(r => r.data),
  // lang: 'en' (fornecedor) | 'pt' (uso interno)
  export: (id, currency_mode, lang) => api.post(`/quotations/${id}/export`, { currency_mode, lang }, { responseType: 'blob' })
    .then(r => ({
      blob: r.data,
      filename: (r.headers['content-disposition'] || '').split('filename="')[1]?.replace('"', '') || `quotation_${id}.xlsx`,
    })),
  // Manifesto de todos os anexos (todas as fases: cotação / pedido / amostra)
  attachments: id => api.get(`/quotations/${id}/attachments`).then(r => r.data),
  // Pacote .zip: todos os anexos + Excel atualizado (com custo efetivo).
  // `only='photos'` devolve um .zip só com as fotos.
  attachmentsZip: (id, currency_mode = 'both', only) =>
    api.get(`/quotations/${id}/attachments.zip`, { params: { currency_mode, ...(only ? { only } : {}) }, responseType: 'blob' })
      .then(r => ({
        blob: r.data,
        filename: (r.headers['content-disposition'] || '').split('filename="')[1]?.replace('"', '') || `${id}_arquivos.zip`,
      })),
  uploadPhotos: (id, files) => {
    const fd = new FormData(); files.forEach(f => fd.append('photos', f));
    return api.post(`/quotations/${id}/photos`, fd).then(r => r.data);
  },
  deletePhoto: id => api.delete(`/photos/${id}`).then(r => r.data),
  importResponse: (id, fd) => api.post(`/quotations/${id}/import-response`, fd).then(r => r.data),
  parseTemplate: fd => api.post('/quotations/parse-template', fd).then(r => r.data),
};

export const reports = {
  quotations: (params) => api.get('/reports/quotations', { params }).then(r => r.data),
  purchases: (params) => api.get('/reports/purchases', { params }).then(r => r.data),
  suppliers: () => api.get('/reports/suppliers').then(r => r.data),
  performance: (params) => api.get('/reports/performance', { params }).then(r => r.data),
  funnel: () => api.get('/reports/funnel').then(r => r.data),
  agents: (params) => api.get('/reports/agents', { params }).then(r => r.data),
};

export const stats = {
  get: () => api.get('/stats').then(r => r.data),
};

export const alertsApi = {
  get: () => api.get('/alerts').then(r => r.data),
  // pedidos com data FOB atrasada ou vencendo em até 7 dias
  fob: () => api.get('/alerts/fob').then(r => r.data),
};

export const exchangeRate = {
  get: () => api.get('/exchange-rate').then(r => r.data),
};

export const orders = {
  list: (params) => api.get('/quotations', { params: { ...params, type: 'orders' } }).then(r => r.data.rows !== undefined ? r.data : { rows: r.data, total: r.data.length, page: 1, pages: 1 }),
  confirmOrder: (id, data) => api.post(`/quotations/${id}/confirm-order`, data).then(r => r.data),
  createSample: id => api.post(`/quotations/${id}/create-sample`).then(r => r.data),
  promoteToBulk: id => api.post(`/quotations/${id}/promote-to-bulk`).then(r => r.data),
  // Transforma uma amostra OU cotação em um PEDIDO BULK novo, levando os itens
  // escolhidos (com fotos e arquivos). data = { items:[{source_item_id, quantity,
  // price, quote_bulk_price}], copy_files, copy_quotation_files, ... }
  createBulk: (id, data) => api.post(`/quotations/${id}/create-bulk`, data).then(r => r.data),
  // Preenche preço/quantidade dos itens de um pedido (ex.: preço bulk que o
  // fornecedor respondeu depois).
  updateItemPrices: (id, items) => api.patch(`/quotations/${id}/item-prices`, { items }).then(r => r.data),
  // NCM dos itens em qualquer fase (simular impostos na Nacionalização). items = [{ id, ncm }]
  updateItemNcm: (id, items) => api.patch(`/quotations/${id}/item-ncm`, { items }).then(r => r.data),
  getPayments: id => api.get(`/quotations/${id}/payments`).then(r => r.data),
  addPayment: (id, fd) => api.post(`/quotations/${id}/payments`, fd).then(r => r.data),
  // Edita um pagamento já lançado (valor, moeda, câmbio, data, categorias,
  // comprovante). `fd` no mesmo formato do cadastro; remove_receipt=1 apaga o anexo.
  updatePayment: (paymentId, fd) => api.put(`/payments/${paymentId}`, fd).then(r => r.data),
  deletePayment: id => api.delete(`/payments/${id}`).then(r => r.data),
  // Documentos do pedido (invoice, packing list, BL…) — em qualquer fase
  uploadDocuments: (id, files, description) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    if (description) fd.append('description', description);
    return api.post(`/quotations/${id}/documents`, fd).then(r => r.data);
  },
  // Invoices e numerários (aba Pagamentos). fields: doc_type, doc_number,
  // amount, currency, issue_date, due_date, payment_id, notes
  addFinancialDocs: (id, files, fields = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
    files.forEach(f => fd.append('files', f));
    return api.post(`/quotations/${id}/financial-documents`, fd).then(r => r.data);
  },
  updateFinancialDoc: (docId, fields) => api.put(`/financial-documents/${docId}`, fields).then(r => r.data),
  deleteFinancialDoc: docId => api.delete(`/financial-documents/${docId}`).then(r => r.data),
  // Histórico de movimentações e alterações do pedido
  events: id => api.get(`/quotations/${id}/events`).then(r => r.data),
  getShipment: id => api.get(`/quotations/${id}/shipment`).then(r => r.data),
  saveShipment: (id, data) => api.put(`/quotations/${id}/shipment`, data).then(r => r.data),
  getExtraCosts: id => api.get(`/quotations/${id}/extra-costs`).then(r => r.data),
  addExtraCost: (id, data) => api.post(`/quotations/${id}/extra-costs`, data).then(r => r.data),
  deleteExtraCost: id => api.delete(`/extra-costs/${id}`).then(r => r.data),
  changeOrderType: (id, order_type) => api.patch(`/quotations/${id}/order-type`, { order_type }).then(r => r.data),
  revertToQuotation: (id) => api.post(`/quotations/${id}/revert-to-quotation`).then(r => r.data),
  updateExchangeRate: (id, data) => api.patch(`/quotations/${id}/exchange-rate`, data).then(r => r.data),
  updateOrderCosts: (id, data) => api.patch(`/quotations/${id}/order-costs`, data).then(r => r.data),
  getBalance: id => api.get(`/quotations/${id}/balance`).then(r => r.data),
  // Prazo de produção + entrega prevista. data = { order_production_days,
  // production_starts_from:'order'|'deposit', delivery_transit_days, estimated_delivery }
  updateDelivery: (id, data) => api.patch(`/quotations/${id}/delivery`, data).then(r => r.data),
  getEffectiveCost: id => api.get(`/quotations/${id}/effective-cost`).then(r => r.data),
  snapshotCost: id => api.post(`/quotations/${id}/snapshot-cost`).then(r => r.data),
};

// Custos tipados por pedido/container + rateio
export const costItems = {
  listForOrder: id => api.get(`/quotations/${id}/cost-items`).then(r => r.data),
  addForOrder: (id, data) => api.post(`/quotations/${id}/cost-items`, data).then(r => r.data),
  delete: id => api.delete(`/cost-items/${id}`).then(r => r.data),
  setAllocation: (id, data) => api.put(`/cost-allocations/${id}`, data).then(r => r.data),
};

// Containers (importação consolidada)
// Consolidações: vários pedidos numa PI (trading), pagamento rateado
export const consolidations = {
  list: () => api.get('/consolidations').then(r => r.data),
  get: id => api.get(`/consolidations/${id}`).then(r => r.data),
  available: currency => api.get('/consolidations/available-orders', { params: { currency } }).then(r => r.data),
  create: data => api.post('/consolidations', data).then(r => r.data),
  update: (id, data) => api.put(`/consolidations/${id}`, data).then(r => r.data),
  remove: id => api.delete(`/consolidations/${id}`).then(r => r.data),
  addOrders: (id, orderIds) => api.post(`/consolidations/${id}/orders`, { order_ids: orderIds }).then(r => r.data),
  removeOrder: (id, qid) => api.delete(`/consolidations/${id}/orders/${qid}`).then(r => r.data),
  previewPayment: (id, data) => api.post(`/consolidations/${id}/payments/preview`, data).then(r => r.data),
  addPayment: (id, fields, receipt) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v); });
    if (receipt) fd.append('receipt', receipt);
    return api.post(`/consolidations/${id}/payments`, fd).then(r => r.data);
  },
  deletePayment: pid => api.delete(`/consolidation-payments/${pid}`).then(r => r.data),
  addDocuments: (id, files, fields = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
    files.forEach(f => fd.append('files', f));
    return api.post(`/consolidations/${id}/documents`, fd).then(r => r.data);
  },
  deleteDocument: did => api.delete(`/consolidation-documents/${did}`).then(r => r.data),
};

export const containers = {
  list: () => api.get('/containers').then(r => r.data),
  get: id => api.get(`/containers/${id}`).then(r => r.data),
  create: data => api.post('/containers', data).then(r => r.data),
  update: (id, data) => api.put(`/containers/${id}`, data).then(r => r.data),
  delete: id => api.delete(`/containers/${id}`).then(r => r.data),
  addOrder: (id, quotation_id) => api.post(`/containers/${id}/orders`, { quotation_id, action: 'add' }).then(r => r.data),
  removeOrder: (id, quotation_id) => api.post(`/containers/${id}/orders`, { quotation_id, action: 'remove' }).then(r => r.data),
  recompute: id => api.post(`/containers/${id}/recompute`).then(r => r.data),
  listCosts: id => api.get(`/containers/${id}/cost-items`).then(r => r.data),
  addCost: (id, data) => api.post(`/containers/${id}/cost-items`, data).then(r => r.data),
};

export const samples = {
  list: () => api.get('/samples').then(r => r.data),
  getTracking: id => api.get(`/quotations/${id}/sample-tracking`).then(r => r.data),
  saveTracking: (id, data) => api.put(`/quotations/${id}/sample-tracking`, data).then(r => r.data),
  getLiveTracking: id => api.get(`/tracking/${id}`).then(r => r.data),
  refreshTracking: (id, body) => api.post(`/tracking/refresh/${id}`, body || {}).then(r => r.data),
};

export const tracking = {
  get:     id => api.get(`/tracking/${id}`).then(r => r.data),
  refresh: (id, body) => api.post(`/tracking/refresh/${id}`, body || {}).then(r => r.data),
};

export const ncmRates = {
  list: () => api.get('/ncm-rates').then(r => r.data),
  get: (ncm) => api.get(`/ncm-rates/${ncm}`).then(r => r.data),
  save: (data) => api.post('/ncm-rates', data).then(r => r.data),
  update: (ncm, data) => api.put(`/ncm-rates/${ncm}`, data).then(r => r.data),
  delete: (ncm) => api.delete(`/ncm-rates/${ncm}`).then(r => r.data),
  alternatives: (ncm) => api.get(`/ncm-rates/${ncm}/alternatives`).then(r => r.data),
};

export const importSettings = {
  get: () => api.get('/import-settings').then(r => r.data),
  update: (data) => api.put('/import-settings', data).then(r => r.data),
};

export const landedCost = {
  simulate: (items, params) => api.post('/landed-cost/simulate', { items, params }).then(r => r.data),
  ofQuotation: (id) => api.get(`/quotations/${id}/landed-cost`).then(r => r.data),
};

export const invoiceImport = {
  parse: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import-invoice/parse', fd).then(r => r.data);
  },
};

export const products = {
  search: (q) => api.get('/products', { params: { q } }).then(r => r.data),
  list: () => api.get('/products').then(r => r.data),
  create: (data) => api.post('/products', data).then(r => r.data),
  update: (id, data) => api.put(`/products/${id}`, data).then(r => r.data),
  history: (id) => api.get(`/products/${id}/history`).then(r => r.data),
};

// Histórico de preços já praticados para o mesmo artigo (informativo)
export const priceHistory = {
  forItem: (item_id) => api.get('/price-history', { params: { item_id } }).then(r => r.data),
  forProduct: (product, product_id) => api.get('/price-history', { params: { product, product_id } }).then(r => r.data),
};

export const agentDocuments = {
  list: id => api.get(`/quotations/${id}/agent-documents`).then(r => r.data),
  upload: (id, fd) => api.post(`/quotations/${id}/agent-documents`, fd).then(r => r.data),
  delete: docId => api.delete(`/agent-documents/${docId}`).then(r => r.data),
};

export default api;
